import { ReactComponent as TouziIcon } from '@/assets/images/svg/touzi.svg';
import CreateDatStepperModal from '@/components/CreateDatStepperModal';
import { FAUCET } from '@/constant';
import useGenerateDat from '@/hooks/useGenerateDat';
import serverConfig from '@/stores/serverConfig';
import { getLazbubuDataConfig } from '@/utils';
import { Link, Skeleton, TextField, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import lazbubuImg from '../../assets/images/lazbubu-default.png';
import RarityBadge from '../RarityBadge';

// Form data type
interface CreateLazbubuForm {
    lazbubuName: string;
    userName: string;
}

// Styled Components
const ContentWrapper = styled.div`
    width: 100%;
    height: 100%;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding: 1rem;
    overflow-y: auto;
`;

const Title = styled(Typography)`
    font-size: 20px;
    color: #000;
    font-weight: 600;
`;

const LazbubuContainer = styled.div`
    border-radius: 20px;
    margin-bottom: 1rem;
    position: relative;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 10px;
    max-width: 240px;
`;

const LazbubuImage = styled.img`
    width: 240px;
    height: 240px;
    object-fit: contain;
    border-radius: 20px;
    overflow: hidden;
`;

const RarityTag = styled.div`
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: #2c44b0;
    color: white;
    padding: 4px 12px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
`;

const FormContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1rem;

    > div {
        display: flex;
        align-items: flex-start;
        flex-direction: column;
        gap: 16px;
    }
`;

const InputLabel = styled(Typography)`
    font-size: 14px !important;
    color: #000 !important;
    margin-bottom: 0.5rem !important;
    text-align: left !important;
`;

const StyledTextField = styled(TextField)`
    & .MuiOutlinedInput-root {
        border-radius: 12px;
        background: #fff;
        height: 40px;

        &:hover fieldset {
            border-color: #ddd;
        }

        &.Mui-focused fieldset {
            border-color: rgba(53, 99, 243, 1);
        }
    }
`;

const PageButton = styled.button`
    width: 160px;
    height: 38px;
    background-color: #000;
    border-radius: 12px;
    margin-top: 20px;
    color: #fff;
    font-size: 14px;
`;

const ClaimLink = styled(Link)`
    color: rgba(53, 99, 243, 1) !important;
    text-decoration: underline !important;
    font-size: 14px !important;
    margin-top: 1rem !important;
    cursor: pointer;

    &:hover {
        text-decoration: underline !important;
    }
`;
const StyledRarityBadge = styled(RarityBadge)`
    position: absolute;
    top: 12px;
    right: 12px;
`;

const StyledTouzi = styled.div<{ $shaking?: boolean }>`
    position: absolute;
    bottom: 12px;
    right: 12px;
    width: 48px;
    height: 48px;
    background: rgba(0, 0, 0, 0.1);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    @keyframes shake {
        0% {
            transform: translate(0, 0) rotate(0deg);
        }
        25% {
            transform: translate(5px, 5px) rotate(5deg);
        }
        50% {
            transform: translate(-5px, 5px) rotate(-5deg);
        }
        75% {
            transform: translate(5px, -5px) rotate(5deg);
        }
        100% {
            transform: translate(0, 0) rotate(0deg);
        }
    }
    ${(props) => props.$shaking && `animation: shake 0.6s ease-in-out;`}
    svg {
        color: #000;
        width: 36px;
        height: 36px;
    }
`;

interface Props {
    lazbubuData: string;
    refetchLazbubuData: () => void;
    canClearLazbubuData?: boolean;
    onCreateSuccess?: () => void;
}

export default function CreateLazbubu({
    lazbubuData,
    refetchLazbubuData,
    canClearLazbubuData = true,
    onCreateSuccess,
}: Props) {
    const lazbubuDataJson = getLazbubuDataConfig(lazbubuData);
    const id = lazbubuDataJson?.uuid;
    const rarity = lazbubuDataJson?.rarity;
    const avatarKey = lazbubuDataJson?.image;
    const [isLoadingAvatars, setIsLoadingAvatars] = useState(false);
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        getValues,
    } = useForm<CreateLazbubuForm>();

    const {
        createLazbubu,
        steps,
        currentStepIndex,
        isCreating,
        isCompleted,
        resetDatCreation,
    } = useGenerateDat(lazbubuData, onCreateSuccess);

    const onSubmit = async (data: CreateLazbubuForm) => {
        console.log('Form submitted with data:', data);
        console.log('Lazbubu uuid:', id);

        try {
            await createLazbubu(data);
        } catch (error) {
            console.error('Failed to create lazbubu:', error);
        }
    };

    const handleModalClose = () => {
        resetDatCreation();
    };

    const handleRetryDatCreation = () => {
        resetDatCreation();
        // 重新提交表单数据
        const formData = getValues();
        if (formData.lazbubuName && formData.userName) {
            createLazbubu(formData);
        }
    };

    const handleClaimToken = () => {
        window.open(FAUCET);
    };

    const getRarity = () => {
        const rarities = ['Common', 'Advanced', 'Rare', 'Epic'];

        return rarities[Number(rarity)] || 'Common';
    };

    const [isShaking, setIsShaking] = useState(false);
    const avatarSrc = useMemo(() => {
        const key = avatarKey;
        const base = serverConfig.config?.avatarBaseUrl;
        if (!key) return lazbubuImg;
        return base ? `${base}/${key}` : key;
    }, [avatarKey]);

    const onClickTouzi = async () => {
        setIsShaking(true);
        await refetchLazbubuData();

        setTimeout(() => {
            setIsShaking(false);
        }, 650);
    };

    return (
        <ContentWrapper>
            <Title>A {getRarity()} Lazbubu!</Title>

            <LazbubuContainer>
                {isLoadingAvatars ? (
                    <Skeleton
                        variant="rectangular"
                        width={240}
                        height={240}
                        sx={{ borderRadius: '20px' }}
                    />
                ) : (
                    <LazbubuImage src={avatarSrc} alt={`${rarity} Lazbubu`} />
                )}
                <StyledRarityBadge rarity={rarity} />
                <StyledTouzi onClick={onClickTouzi} $shaking={isShaking}>
                    <TouziIcon />
                </StyledTouzi>
            </LazbubuContainer>

            <FormContainer>
                <div>
                    <InputLabel>Give Lazbubu a name</InputLabel>
                    <StyledTextField
                        fullWidth
                        variant="outlined"
                        placeholder="Enter Lazbubu's name"
                        {...register('lazbubuName', {
                            required: 'Lazbubu name is required',
                            minLength: {
                                value: 2,
                                message: 'Name must be at least 2 characters',
                            },
                            maxLength: {
                                value: 20,
                                message: 'Name must be less than 20 characters',
                            },
                        })}
                        error={!!errors.lazbubuName}
                        helperText={errors.lazbubuName?.message}
                    />
                </div>

                <div>
                    <InputLabel>Tell Lazbubu your name</InputLabel>
                    <StyledTextField
                        fullWidth
                        variant="outlined"
                        placeholder="Enter your name"
                        {...register('userName', {
                            required: 'Your name is required',
                            minLength: {
                                value: 2,
                                message: 'Name must be at least 2 characters',
                            },
                            maxLength: {
                                value: 30,
                                message: 'Name must be less than 30 characters',
                            },
                        })}
                        error={!!errors.userName}
                        helperText={errors.userName?.message}
                    />
                </div>
            </FormContainer>

            <PageButton onClick={handleSubmit(onSubmit)}>Mint</PageButton>

            <ClaimLink onClick={handleClaimToken}>
                Claim testnet token
            </ClaimLink>

            <CreateDatStepperModal
                open={isCreating}
                onClose={handleModalClose}
                steps={steps}
                currentStepIndex={currentStepIndex}
                isCompleted={isCompleted}
                onRetry={handleRetryDatCreation}
            />
        </ContentWrapper>
    );
}
